import React from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { token } from '@bynder/design-system';
import { splitIds } from '../../helpers/utils';
import Section from './Section';
import { AddSectionButton } from './AddSectionButton';
import { useGetThemesetColors } from '../../queries/themesets';
import { useWidgetMutations } from '../../mutations/widgets/widgets';
import { useGuideStore } from '../../stores/guideStore';
import { usePageStore } from '../../stores/pageStore';
import { defaultWhite } from '../widgets/RichText/Config';
import { Color } from '../../types/color';

const SectionList: React.FC<{
  sections: Section[];
  isPageEditable: boolean;
  defaultSectionBackgroundColor: Color | null;
}> = ({ sections, isPageEditable, defaultSectionBackgroundColor }) => {
  const isEditMode = useGuideStore(s => s.isEditMode);
  const activePageId = useGuideStore(s => s.activePageId);
  const lastSectionBackgroundColor = usePageStore(
    s => s.lastSectionBackgroundColor,
  );
  const setLastSectionBackgroundColor = usePageStore(
    s => s.setLastSectionBackgroundColor,
  );
  const { moveWidget } = useWidgetMutations();
  const customColors = useGetThemesetColors();

  const isEditable = isEditMode && isPageEditable;
  const lastColorId = sections[sections.length - 1]?.backgroundColorId;

  React.useEffect(() => {
    const getLastSectionBackgroundColor = () => {
      const fallbackColor = defaultWhite;
      const themesetBackgroundColor = defaultSectionBackgroundColor;
      const activeBackgroundColor = customColors.find(
        c => c.id === lastColorId,
      );
      if (activeBackgroundColor) {
        return activeBackgroundColor;
      } else if (themesetBackgroundColor) {
        return themesetBackgroundColor;
      }
      return fallbackColor;
    };
    const lastSectionBackgroundColor = getLastSectionBackgroundColor();
    setLastSectionBackgroundColor(lastSectionBackgroundColor.hexCode);
  }, [
    customColors,
    defaultSectionBackgroundColor,
    lastColorId,
    setLastSectionBackgroundColor,
  ]);

  const onDragStart = () => {};

  const onDragEnd = ({ source, destination, draggableId }: DropResult) => {
    if (
      !destination ||
      (destination.droppableId === source.droppableId &&
        destination.index === source.index)
    ) {
      return;
    }

    // need to split out the combined sectionId and columnIndex again
    const [sectionId, columnIndex] = splitIds(source.droppableId);
    const [toSectionId, toColumnIndex] = splitIds(destination.droppableId);

    moveWidget.mutate({
      fromSectionId: sectionId,
      fromColumnIndex: Number(columnIndex),
      widgetId: draggableId,
      toSectionId,
      toColumnIndex: Number(toColumnIndex),
      toPosition: destination.index,
    });
  };

  const sectionCount = sections.length;

  if (isPageEditable && isEditMode) {
    return (
      <>
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <StyledSectionContainer>
            {sections.map((section, index) => (
              <Section
                defaultBackgroundColor={defaultSectionBackgroundColor}
                index={index}
                pageId={activePageId!}
                key={section.id}
                section={section}
                sectionCount={sectionCount}
                isEditable={isEditable}
              />
            ))}
          </StyledSectionContainer>
          {!sectionCount && (
            <SectionButtonContainer>
              <AddSectionButton sectionIndex={0} sectionCount={0} isVisible />
            </SectionButtonContainer>
          )}
        </DragDropContext>
        <StyledPageBottomFiller $backgroundColor={lastSectionBackgroundColor} />
      </>
    );
  }

  return (
    <>
      <ul>
        {sections.map((section, index) => (
          <Section
            defaultBackgroundColor={defaultSectionBackgroundColor}
            index={index}
            pageId={activePageId!}
            key={section.id}
            section={section}
            sectionCount={sectionCount}
            isEditable={false}
          />
        ))}
      </ul>
      <StyledPageBottomFiller $backgroundColor={lastSectionBackgroundColor} />
    </>
  );
};

export default SectionList;

const StyledSectionContainer = styled.ul`
  > li:first-child {
    border-top: 3px solid ${token.brandPrimary100};
  }

  > li {
    border-bottom: 3px solid ${token.brandPrimary100};
  }
`;

const StyledPageBottomFiller = styled.div<{ $backgroundColor: string }>`
  flex: 1 1 auto;
  background-color: ${props => props.$backgroundColor ?? 'inherit'};
`;

const SectionButtonContainer = styled.div`
  display: flex;
  height: 200px;
  justify-content: center;
  align-items: flex-end;
`;
